import { cloneDeep } from "lodash";

const HIGHEST_NUMBER = 99999999;

// Atomic defaults
const defaultInput = (value = 0, input = "Unit") => ({ value, input });
const defaultProduct = (value = 0.0, input = "Percent", product = "") => ({ value, input, product });

// Composed defaults
const inputCommissionMultiple = (definitions = []) => {
	return definitions.reduce((acc, item) => {
		const { key, value = 0, input } = item;
		acc[key] = defaultInput(value, input);
		return acc;
	}, {});
};

const doubleCommission = (from, to, args) => {
	const { firstCommission, secondCommission, firstKey = "cash", secondKey = "lease" } = args || {};
	return {
		from: defaultInput(...from),
		to: defaultInput(...to),
		[firstKey]: defaultInput(...firstCommission),
		[secondKey]: defaultInput(...secondCommission),
	};
};

// _____________________________________ATOMIC TIERS________________________________________________

const commissionTier = (from = 0, to = 0, commissions = []) => {
	const row = inputCommissionMultiple([
		{ key: "from", value: from, input: "Percent" },
		{ key: "to", value: to, input: "Percent" },
	]);
	return { ...row, commissions };
};

const complexCommission = (from, to, args) => {
	const { identifier = "bonus", commission } = args || {};
	return {
		from: defaultInput(...from),
		to: defaultInput(...to),
		[identifier]: defaultInput(...commission),
	};
};

const complexCommissionTier = (from, to, args) => {
	const { identifier = "commissions", commissions = [] } = args || {};
	return {
		from: defaultInput(...from),
		to: defaultInput(...to),
		[identifier]: commissions,
	};
};

const durationTier = (args) => {
	const { from = 0, to = 0, commission = 0, commissionInput = "Percent" } = args || {};
	return inputCommissionMultiple([
		{ key: "from", value: from, input: "Duration" },
		{ key: "to", value: to, input: "Duration" },
		{ key: "commission", value: commission, input: commissionInput },
	]);
};

const ordinalTier = (args) => {
	const { from = 1, to = 15000, bonus = 0, bonusInput = "Percent" } = args || {};
	return inputCommissionMultiple([
		{ key: "from", value: from, input: "Ordinal" },
		{ key: "to", value: to, input: "Ordinal" },
		{ key: "bonus", value: bonus, input: bonusInput },
	]);
};

const growthBonusTier = (from = 0, to = 0, definition = { bonus: 0, input: "Percent" }) => {
	return inputCommissionMultiple([
		{ key: "from", value: from, input: "Percent" },
		{ key: "to", value: to, input: "Percent" },
		{ key: "bonus", value: definition?.bonus || 0, input: definition?.input || "Percent" },
	]);
};

const simpleCommission = (from, to, args) => {
	const { identifier = "bonus", commission = 0, input = "Dollar" } = args || {};
	return inputCommissionMultiple([
		{ key: "from", value: from || 0 },
		{ key: "to", value: to || 0 },
		{ key: identifier, value: commission, input },
	]);
};

const simpleProductsTier = (from, to, args) => {
	const {
		frontGross = defaultInput(0, "Percent"),
		backGross = defaultInput(0, "Percent"),
		frontProducts = [],
		backProducts = [],
	} = args || {};

	return {
		from: from || defaultInput(),
		to: to || defaultInput(10000),
		frontGross,
		backGross,
		frontProducts,
		backProducts,
	};
};

// _________________________________________________________________________________________________

const adjustmentFn = (from = 0, to = 0, adjustment = 0) =>
	inputCommissionMultiple([
		{ key: "from", value: from, input: "Percent" },
		{ key: "to", value: to, input: "Percent" },
		{ key: "adjustment", value: adjustment, input: "Percent" },
	]);

const callActivityPerformanceDefault = () => ({
	tiers: [commissionTier(100, 0, [durationTier({ from: 744, to: 0, commission: 0, commissionInput: "Percent" })])],
	individual: true,
	hasQueues: false,
	grossProfitSource: "SUBTOTAL",
	evaluateBy: "COMMISSION_CYCLE",
});

const metricTierItem = (missed, attained, numberRequired = 0) => {
	return {
		missed,
		attained,
		numberRequired,
	};
};

const makeMetrics = (reference = "", numberRequired = 0, metrics = []) => ({
	reference,
	metrics,
	percentOfTotal: false,
	tiers: [metricTierItem(defaultInput(0, "Percent"), defaultInput(0, "Percent"), numberRequired)],
});

const metric = (make, attributes = null) => ({
	make,
	...attributes,
});

const customerSatisfactionDefault = (
	makes = [],
	excludesMakes = ["Cadillac"],
	definitions = { useOnlyStoreMetrics: false, useOnlyIndividualMetrics: false }
) => {
	const { useOnlyStoreMetrics, useOnlyIndividualMetrics } = definitions;
	const filteredMakes = makes.filter((make) => !excludesMakes.includes(make.franchise));
	const metrics = filteredMakes.map((make) => {
		const makeAttributes = {};
		if (!useOnlyStoreMetrics) {
			makeAttributes["individualMakeMetrics"] = makeMetrics();
		}
		if (!useOnlyIndividualMetrics) {
			makeAttributes["storeMakeMetrics"] = makeMetrics();
		}
		if (!useOnlyStoreMetrics && !useOnlyIndividualMetrics) {
			makeAttributes["evaluateStoreMetrics"] = false;
		}

		return metric(make.franchise, { ...makeAttributes, oemReference: make?.oemReference ?? "", version: "" });
	});

	return {
		makeMetrics: metrics,
		weighting: {
			weightingType: "UNITS",
			customWeights: [],
		},
	};
};

const adjustmentDefaultTo = 500;
const reputationAdjustment = () => [adjustmentFn(), adjustmentFn(0, adjustmentDefaultTo, 0)];

const longevityDefault = (percentDefault = false) => {
	const inputDefault = percentDefault ? "Percent" : "Dollar";
	return [
		doubleCommission([0, "Unit"], [100, "Unit"], {
			firstKey: "amount",
			secondKey: "allowance",
			firstCommission: [0, inputDefault],
			secondCommission: [0, "Dollar"],
		}),
	];
};

const marketShareTier = (from = 0, to = 100, bonuses = []) => ({
	from: defaultInput(from, "Percent"),
	to: defaultInput(to, "Percent"),
	bonuses,
});

const marketShareDefault = (franchises = []) => {
	const minGrowth = 100;
	const maxGrowth = 10000;
	const growthTier = complexCommission([-minGrowth, "Percent"], [maxGrowth, "Percent"], { commission: [0, "Percent"] });
	const makes = franchises.map((make) => ({
		make,
		frequency: "MoM",
		tiers: [marketShareTier(0, 100, [growthTier])],
	}));
	return {
		makes,
		simultaneousMarketShare: false,
	};
};

const topPerformanceBonusDefault = () => [defaultInput(0, "Dollar")];

const volumeBonusTier = (args) => {
	const { from = 0, to = 0, bonus = 0, bonusInput = "Percent" } = args || {};
	return complexCommission([from, "Unit"], [to, "Unit"], { commission: [bonus, bonusInput] });
};

const volumeBonusesTier = (from = 1, to = 100, bonuses = []) => ({
	from: defaultInput(from, "Dollar"),
	to: defaultInput(to, "Dollar"),
	bonuses,
});

const volumeBonusDefinition = (make, tiers = [], applyTiersRetroactively = false) => ({
	make,
	tiers,
	applyTiersRetroactively,
	adjustments: [],
	forgiveness: defaultInput(0, "Unit"),
	subjectToReputation: false,
});

const variableOperationsGpsBonusTier = (args) => {
	const { from = 0, to = 0, bonus = 0, bonusInput = "Dollar", fromInput = "Percent", toInput = "Percent" } = args || {};
	return {
		from: defaultInput(from, fromInput),
		to: defaultInput(to, toInput),
		bonus: defaultInput(bonus, bonusInput),
	};
};

const volumeBonusDefault = (franchises = []) => {
	const max = 100000000;
	const bonuses = franchises.map((franchise) =>
		volumeBonusDefinition(franchise, [
			volumeBonusesTier(-max, max, [volumeBonusTier({ from: 0, to: 1000, bonus: 0, bonusInput: "Dollar" })]),
		])
	);
	return {
		bonuses,
		franchises,
		newAndPreOwnedRequired: false,
		combineFranchises: false,
		globalUnitCounts: false,
		preOwned: volumeBonusDefinition("Pre-Owned Vehicles", [
			volumeBonusesTier(-max, max, [volumeBonusTier({ from: 0, to: 1000, bonus: 0, bonusInput: "Dollar" })]),
		]),
		combined: volumeBonusDefinition("Combined", [
			volumeBonusesTier(-max, max, [volumeBonusTier({ from: 0, to: 1000, bonus: 0, bonusInput: "Dollar" })]),
		]),
	};
};

const buildVariableOperationsGpsBonus = (args) => {
	const { from = 0, fromInputType = "Percent", to = 0, toInputType = "Percent", bonus = 0, bonusInputType = "Dollar" } = args || {};
	return {
		from: defaultInput(from, fromInputType),
		to: defaultInput(to, toInputType),
		bonus: defaultInput(bonus, bonusInputType),
	};
};

const buildVariableOperationsGpsBonusTier = (args) => {
	const { from = 0, fromInputType = "Percent", to = 0, toInputType = "Percent", bonuses = [] } = args || {};
	return {
		from: defaultInput(from, fromInputType),
		to: defaultInput(to, toInputType),
		bonuses,
	};
};

const buildVariableOperationsGpsBonusSetup = (args) => {
	const {
		fromTier = HIGHEST_NUMBER,
		columnInputType = "Percent",
		rowInputType = "Percent",
		bonusInputType = "Dollar",
		defaultRows = 1,
		defaultColumns = 1,
		franchises = [],
	} = args || {};
	const tiers = [];

	for (let i = 0; i < defaultRows; i++) {
		const bonuses = [];
		for (let j = 0; j < defaultColumns; j++) {
			const isFirstColumn = j === 0;
			const isLastColumn = j === defaultColumns - 1;
			bonuses.push(
				buildVariableOperationsGpsBonus({
					from: isFirstColumn ? HIGHEST_NUMBER : 0,
					fromInputType: columnInputType,
					to: isLastColumn ? -HIGHEST_NUMBER : 0,
					toInputType: columnInputType,
					bonus: 0,
					bonusInputType,
				})
			);
		}
		tiers.push(
			buildVariableOperationsGpsBonusTier({
				from: fromTier,
				fromInputType: rowInputType,
				to: -HIGHEST_NUMBER,
				toInputType: rowInputType,
				bonuses,
			})
		);
	}

	return {		
		newVehicles: franchises.map((franchise) => ({ 
			make: franchise, 
			tiers: cloneDeep(tiers),
			evaluationType: "ACTIVATION"
		})) || [],
		preOwned: { make: "Pre-Owned Vehicles", tiers: cloneDeep(tiers), evaluationType: "ACTIVATION" },
		combined: { make: "New Vehicles", tiers: cloneDeep(tiers), evaluationType: "ACTIVATION" },
		globalUnitCounts: false,
		combineFranchises: false,
	};
};

// _________________________________________________________________________________________________

const getMiniDealModel = () => ({
	mini: defaultInput(0, "Dollar"),
	cap: defaultInput(0, "Dollar"),
	capEnabled: false,
});

const model = (make = "", addons = null) => {
	const { regularExpression = false, miniDeal = false, applyRetroactively = false } = addons || {};

	const modelDefinition = {
		make,
		model: "",
		modelYears: { rangeType: "RELATIVE", left: null, right: null },
		tiers: [simpleProductsTier()],
	};

	if (regularExpression) {
		modelDefinition["regularExpression"] = "";
	}

	if (miniDeal) {
		modelDefinition["miniDeal"] = getMiniDealModel();
	}

	if (applyRetroactively) {
		modelDefinition["applyTiersRetroactively"] = false;
	}

	return modelDefinition;
};

// _________________________________________________________________________________________________

const getPayLabel = (payType = "Customer") => `${payType} Pay`;

const getPayment = (payType = "Customer", global = false) => {
	return {
		payType,
		global,
		payLabel: getPayLabel(payType),
	};
};

const commissionPayment = (defaultType, args) => {
	const { commissionDefinition } = args || {};
	const { value = 0, input = "Dollar" } = commissionDefinition || {};
	return {
		payment: getPayment(defaultType),
		commission: defaultInput(value, input),
	};
};

const retentionPayDefault = () => {
	return {
		retention: defaultInput(0, "Percent"),
	};
};

// _________________________________________________________________________________________________

export {
	adjustmentFn as Adjustment,
	buildVariableOperationsGpsBonus as BuildVariableOperationsGpsBonus,
	buildVariableOperationsGpsBonusSetup as BuildVariableOperationsGpsBonusSetup,
	buildVariableOperationsGpsBonusTier as BuildVariableOperationsGpsBonusTier,
	callActivityPerformanceDefault as CallActivityPerformanceDefault,
	commissionPayment as CommissionPayment,
	commissionTier as CommissionTier,
	complexCommission as ComplexCommission,
	complexCommissionTier as ComplexCommissionTier,
	customerSatisfactionDefault as CustomerSatisfactionDefault,
	defaultInput as DefaultInput,
	defaultProduct as DefaultProduct,
	doubleCommission as DoubleCommission,
	durationTier as DurationTier,
	growthBonusTier as GrowthBonusTier,
	longevityDefault as LongevityDefault,
	makeMetrics as MakeMetrics,
	marketShareDefault as MarketShareDefault,
	marketShareTier as MarketShareTier,
	metric as Metric,
	metricTierItem as MetricTierItem,
	model as Model,
	ordinalTier as OrdinalTier,
	reputationAdjustment as ReputationAdjustment,
	retentionPayDefault as RetentionPayDefault,
	simpleCommission as SimpleCommission,
	simpleProductsTier as SimpleProductsTier,
	topPerformanceBonusDefault as TopPerformanceBonusDefault,
	volumeBonusDefault as VolumeBonusDefault,
	volumeBonusDefinition as VolumeBonusDefinition,
	volumeBonusesTier as VolumeBonusesTier,
	volumeBonusTier as VolumeBonusTier,
};
